import config from "config/config";
import { authService } from "./authService";
import { createUrlSearchParams } from "helpers";

export const salesService = {
    fetchSalesOrderList,
    createSalesOrder,
    fetchSingleSalesOrder,
    fetchSalesDashboardStats,
    fetchSalesTopAndLeast,
    fetchSalesVelocityStats,
    fetchSalesChannelStats,
    fetchCustomerList,
    createSalesShipment,
    updateSalesOrder,
    fetchTopAndLeastSalesType,
    fetchTopAndLeastRetailSales,
    updateMultipleSalesOrderStatus,
    fetchSalesSummary,
    deleteSalesOrder,
    fetchSalesOrderAgents,
    // Pin/Unpin functionality
    pinSalesOrder,
    unpinSalesOrder,
    bulkPinSalesOrder,
    bulkUnpinSalesOrder,
    // Sales Settings functionality
    getSalesSettings,
    updateSalesSettings,
};

async function fetchSalesOrderList(
    page?: string | number,
    status?: string,
    search?: string,
    warehouseId?: string,
    startDate?: string,
    endDate?: string,
    channel?: string,
    saleType?: string,
    customerEmail?: string,
    pin?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/list/sales-order?page=${page}${
            status ? `&status=${status}` : ""
        }${search ? `&search=${search}` : ""}${warehouseId ? `&warehouseId=${warehouseId}` : ""}${
            startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ""
        }${channel ? `&channel=${channel}` : ""}${saleType ? `&saleType=${saleType}` : ""}${
            customerEmail ? `&customerEmail=${customerEmail}` : ""
        }${pin ? `&pin=${pin}` : ""}`,
        requestOptions
    );

    return res;
}

async function createSalesOrder(data: Record<string, any> | Array<Record<string, any>>) {
    const requestOptions = {
        method: "POST",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sales-order`,
        requestOptions
    );

    return res;
}

async function fetchSingleSalesOrder(orderId: string) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sales-order/${orderId}`,
        requestOptions
    );

    return res;
}

async function updateSalesOrder(
    orderId: string,
    data: Record<string, any> | Array<Record<string, any>>
) {
    const requestOptions = {
        method: "PUT",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sales-order/${orderId}`,
        requestOptions
    );

    return res;
}

async function fetchSalesDashboardStats(startDate?: string, endDate?: string) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/stats${
            startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ""
        }`,
        requestOptions
    );

    return res;
}

async function fetchSalesTopAndLeast(startDate?: string, endDate?: string) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/top-and-least-selling${
            startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ""
        }`,
        requestOptions
    );

    return res;
}

async function fetchSalesVelocityStats(
    startDate?: string,
    endDate?: string,
    groupBy?: "warehouse" | "channel",
    inventoryId?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/sales-velocity?${
            groupBy ? `&groupBy=${groupBy}` : ""
        }${inventoryId ? `&inventoryId=${inventoryId}` : ""}${
            startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ""
        }`,
        requestOptions
    );

    return res;
}

async function fetchSalesChannelStats(startDate?: string, endDate?: string) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/channel-stats?${
            startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ""
        }`,
        requestOptions
    );

    return res;
}

async function fetchCustomerList(
    page?: string | number,
    limit?: string | number,
    search?: string,
    saleType?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/list-customers?page=${page}${
            limit ? `&limit=${limit}` : ""
        }${search ? `&search=${search}` : ""}${saleType ? `&saleType=${saleType}` : ""}`,
        requestOptions
    );

    return res;
}

async function createSalesShipment(data: Record<string, any> | Array<Record<string, any>>) {
    const requestOptions = {
        method: "POST",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sps-order-shipment`,
        requestOptions
    );

    return res;
}

async function fetchTopAndLeastSalesType(startDate?: string, endDate?: string, saleType?: string) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/top-and-least-selling-customers${
            startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ""
        }${saleType ? `&saleType=${saleType}` : ""}`,
        requestOptions
    );

    return res;
}

async function fetchTopAndLeastRetailSales(
    startDate?: string,
    endDate?: string,
    saleType?: string
) {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/dashboard/top-and-least-selling-customers${
            startDate && endDate ? `?startDate=${startDate}&endDate=${endDate}` : ""
        }${saleType ? `&saleType=${saleType}` : ""}`,
        requestOptions
    );

    return res;
}
async function updateMultipleSalesOrderStatus(
    data: Record<string, any> | Array<Record<string, any>>
) {
    const requestOptions = {
        method: "PATCH",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sales-order/multiple-update-status`,
        requestOptions
    );

    return res;
}

async function fetchSalesSummary(data: Record<string, any>) {
    const requestOptions = {
        method: "POST",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/sales-order-summary`,
        requestOptions
    );

    return res;
}

async function deleteSalesOrder(data: string[]) {
    const requestOptions = {
        method: "POST",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/delete-multiple-sale-orders`,
        requestOptions
    );

    return res;
}

async function fetchSalesOrderAgents(
    page?: string | number,
    limit?: string | number,
    filters?: Record<string, any>
) {
    const requestOptions = {
        method: "GET",
    };

    const urlParams = createUrlSearchParams({
        page,
        limit,
        ...filters,
    });

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/list/sales-order-agents?${
            Boolean(urlParams?.toString()) ? urlParams?.toString() : ""
        }`,
        requestOptions
    );

    return res;
}

// Pin/Unpin functionality
async function pinSalesOrder(saleId: string) {
    const requestOptions = {
        method: "PUT",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/pin/${saleId}`,
        requestOptions
    );

    return res;
}

async function unpinSalesOrder(saleId: string) {
    const requestOptions = {
        method: "PUT",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/unpin/${saleId}`,
        requestOptions
    );

    return res;
}

async function bulkPinSalesOrder(data: { saleIds: string[] }) {
    const requestOptions = {
        method: "PUT",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/bulk/pin`,
        requestOptions
    );

    return res;
}

async function bulkUnpinSalesOrder(data: { saleIds: string[] }) {
    const requestOptions = {
        method: "PUT",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/bulk/unpin`,
        requestOptions
    );

    return res;
}

// Sales Settings functionality
async function getSalesSettings() {
    const requestOptions = {
        method: "GET",
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/settings`,
        requestOptions
    );

    return res;
}

async function updateSalesSettings(data: Record<string, any>) {
    const requestOptions = {
        method: "PUT",
        body: JSON.stringify(data),
    };

    const res = await authService.apiGate(
        `${config.API_URL}/api/v1/ldb/sales/settings`,
        requestOptions
    );

    return res;
}
